import React from 'react';
import { motion } from 'framer-motion';
import { WizardStep } from '../types/wizard';

/**
 * Progress bar component props
 */
interface ProgressBarProps {
    currentStep: WizardStep;
    className?: string;
}

/**
 * Animated progress bar showing wizard completion progress
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
    currentStep,
    className = '',
}) => {
    // Calculate progress percentage based on current step (1-3)
    const progressPercentage = ((currentStep - 1) / 2) * 100;

    // Step labels for accessibility
    const stepLabels = {
        1: 'Cadastro',
        2: 'Cotas',
        3: 'Priorização',
    };

    return (
        <div className={`w-full ${className}`} role="progressbar" aria-valuenow={currentStep} aria-valuemin={1} aria-valuemax={3}>
            {/* Step indicators */}
            <div className="flex justify-between items-center mb-4">
                {[1, 2, 3].map((step) => {
                    const isActive = step === currentStep;
                    const isCompleted = step < currentStep;

                    return (
                        <div
                            key={step}
                            className="flex flex-col items-center"
                            aria-label={`Etapa ${step}: ${stepLabels[step as WizardStep]}`}
                        >
                            {/* Step circle */}
                            <motion.div
                                className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-display font-semibold text-sm
                  ${isCompleted
                                        ? 'bg-dexco-green-primary text-dexco-white'
                                        : isActive
                                            ? 'bg-dexco-green-primary text-dexco-white ring-4 ring-dexco-green-primary ring-opacity-20'
                                            : 'bg-gray-200 text-gray-500'
                                    }
                `}
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{
                                    scale: isActive ? 1.1 : 1,
                                    opacity: 1,
                                    transition: { duration: 0.3, ease: 'easeOut' }
                                }}
                                whileHover={{ scale: isActive ? 1.15 : 1.05 }}
                            >
                                {isCompleted ? (
                                    <motion.svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.5, ease: 'easeOut' }}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </motion.svg>
                                ) : (
                                    step
                                )}
                            </motion.div>

                            {/* Step label */}
                            <motion.span
                                className={`
                  mt-2 text-xs font-body text-center max-w-20
                  ${isActive
                                        ? 'text-dexco-green-primary font-semibold'
                                        : isCompleted
                                            ? 'text-dexco-text-dark'
                                            : 'text-gray-500'
                                    }
                `}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 * step, duration: 0.3 }}
                            >
                                {stepLabels[step as WizardStep]}
                            </motion.span>
                        </div>
                    );
                })}
            </div>

            {/* Progress bar track */}
            <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                {/* Progress bar fill */}
                <motion.div
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-dexco-green-primary to-dexco-green-light rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{
                        duration: 0.8,
                        ease: 'easeInOut',
                        delay: 0.2
                    }}
                />

                {/* Animated shimmer effect */}
                <motion.div
                    className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'linear',
                        delay: 1
                    }}
                />
            </div>

            {/* Progress text */}
            <div className="flex justify-between items-center mt-2">
                <motion.span
                    className="text-xs font-body text-dexco-text-dark"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    Etapa {currentStep} de 3
                </motion.span>

                <motion.span
                    className="text-xs font-body text-dexco-green-primary font-semibold"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                >
                    {Math.round(progressPercentage)}% concluído
                </motion.span>
            </div>
        </div>
    );
}; 